'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import MFAVerifyModal from './MFAVerifyModal';
import MFAEnrollModal from './MFAEnrollModal';

type LoginForm = { email: string; password: string; soloCenter: string; role: string; remember: boolean };
type RegisterForm = { email: string; password: string; confirmPassword: string; soloCenter: string; role: string; fullName: string };

const roles = [
  { value: 'onkolog', label: 'Onkolog kliniczny', desc: 'Prowadzi leczenie systemowe, przewodniczy konsylium' },
  { value: 'radioterapeuta', label: 'Radioterapeuta', desc: 'Kwalifikuje i planuje radioterapię' },
  { value: 'chirurg', label: 'Chirurg onkologiczny', desc: 'Kwalifikuje do leczenia operacyjnego' },
  { value: 'radiolog', label: 'Radiolog', desc: 'Interpretuje obrazowanie diagnostyczne' },
  { value: 'patolog', label: 'Patolog', desc: 'Weryfikuje rozpoznanie histopatologiczne' },
  { value: 'koordynator', label: 'Koordynator leczenia', desc: 'Organizuje ścieżkę pacjenta w KSO' },
  { value: 'admin', label: 'Administrator SOLO III', desc: 'Nadzoruje sieć SOLO I/II/III' },
];

const soloCenters = [
  { value: 'solo3-gliwice', label: 'NIO Gliwice — SOLO III · Gliwice (WOM Śląsk)', tier: 'SOLO III' },
  { value: 'solo2-katowice', label: 'Górnośląskie Centrum Onkologii — SOLO II · Katowice', tier: 'SOLO II' },
  { value: 'solo2-bielsko', label: 'Szpital Onkologiczny — SOLO II · Bielsko-Biała', tier: 'SOLO II' },
  { value: 'solo1-czestochowa', label: 'Szpital im. Biegańskiego — SOLO I · Częstochowa', tier: 'SOLO I' },
  { value: 'solo1-sosnowiec', label: 'Szpital Specjalistyczny — SOLO I · Sosnowiec', tier: 'SOLO I' },
  { value: 'solo1-rybnik', label: 'Szpital Rejonowy — SOLO I · Rybnik', tier: 'SOLO I' },
];

const demoCredentials = [
  { role: 'Onkolog kliniczny', email: 'rozkwitaj@solo3.gliwice', password: 'Konsylium2026!', solo: 'SOLO III · Gliwice' },
  { role: 'Koordynator', email: 'a.nowak@solo3.gliwice', password: 'Koordynator2026!', solo: 'SOLO III · Gliwice' },
  { role: 'Radioterapeuta', email: 'p.wisniewski@solo2.gliwice', password: 'Radioterapia2026!', solo: 'SOLO II · Katowice' },
  { role: 'Administrator', email: 'admin@solo3.gliwice', password: 'Admin2026!', solo: 'SOLO III · Gliwice' },
];

export default function LoginScreen() {
  const router = useRouter();
  const supabase = createClient();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');

  // MFA state
  const [mfaVerifyFactorId, setMfaVerifyFactorId] = useState<string | null>(null);
  const [showMFAEnroll, setShowMFAEnroll] = useState(false);

  const loginForm = useForm<LoginForm>({
    defaultValues: { email: '', password: '', soloCenter: '', role: 'onkolog', remember: false },
  });

  const registerForm = useForm<RegisterForm>({
    defaultValues: { email: '', password: '', confirmPassword: '', soloCenter: '', role: 'onkolog', fullName: '' },
  });

  const autofill = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
    toast.info('Dane logowania wypełnione automatycznie');
  };

  // Check if user has MFA enrolled and needs to verify
  const checkMFARequired = async (): Promise<{ required: boolean; factorId?: string }> => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error || !data) return { required: false };
      const totpFactors = data.totp?.filter(f => f.status === 'verified') || [];
      if (totpFactors.length > 0) {
        return { required: true, factorId: totpFactors[0].id };
      }
      return { required: false };
    } catch {
      return { required: false };
    }
  };

  const onLoginSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        // Fallback to demo credentials check
        const valid = demoCredentials.some(c => c.email === data.email && c.password === data.password);
        if (!valid) {
          toast.error('Nieprawidłowe dane logowania — użyj kont demonstracyjnych poniżej');
          setIsLoading(false);
          return;
        }
        toast.success('Zalogowano pomyślnie — witaj w OncoConsil');
        setIsLoading(false);
        router.push('/consilium-dashboard');
        return;
      }

      // Check if MFA is required
      const mfaCheck = await checkMFARequired();
      if (mfaCheck.required && mfaCheck.factorId) {
        setIsLoading(false);
        setMfaVerifyFactorId(mfaCheck.factorId);
        return;
      }

      toast.success('Zalogowano pomyślnie — witaj w OncoConsil');
      setIsLoading(false);
      router.push('/consilium-dashboard');
    } catch (err: any) {
      toast.error(err.message || 'Błąd logowania');
      setIsLoading(false);
    }
  };

  const onRegisterSubmit = async (data: RegisterForm) => {
    if (data.password !== data.confirmPassword) {
      toast.error('Hasła nie są zgodne');
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.fullName,
            role: data.role,
            solo_center: data.soloCenter,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;

      toast.success('Konto utworzone — skonfiguruj uwierzytelnianie 2FA dla bezpieczeństwa PHI');
      setIsLoading(false);
      setShowMFAEnroll(true);
    } catch (err: any) {
      toast.error(err.message || 'Błąd rejestracji');
      setIsLoading(false);
    }
  };

  const handleMFAVerifySuccess = () => {
    setMfaVerifyFactorId(null);
    toast.success('Zalogowano pomyślnie — witaj w OncoConsil');
    router.push('/consilium-dashboard');
  };

  const handleMFAVerifyCancel = async () => {
    await supabase.auth.signOut();
    setMfaVerifyFactorId(null);
    toast.info('Logowanie anulowane');
  };

  const handleMFAEnrollSuccess = () => {
    setShowMFAEnroll(false);
    router.push('/consilium-dashboard');
  };

  const handleMFAEnrollSkip = () => {
    setShowMFAEnroll(false);
    router.push('/consilium-dashboard');
  };

  return (
    <div className="min-h-screen flex">
      {/* MFA Verify Modal */}
      {mfaVerifyFactorId && (
        <MFAVerifyModal
          factorId={mfaVerifyFactorId}
          onSuccess={handleMFAVerifySuccess}
          onCancel={handleMFAVerifyCancel}
        />
      )}

      {/* MFA Enroll Modal */}
      {showMFAEnroll && (
        <MFAEnrollModal
          onSuccess={handleMFAEnrollSuccess}
          onSkip={handleMFAEnrollSkip}
        />
      )}

      {/* Brand panel */}
      <div className="hidden lg:flex flex-col w-[480px] xl:w-[560px] kso-gradient p-10 text-white relative overflow-hidden shrink-0">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 70%, #ffffff 0%, transparent 60%)' }} />
        <div className="flex items-center gap-3 mb-12">
          <AppLogo size={40} />
          <span className="text-2xl font-bold tracking-tight">OncoConsil</span>
        </div>
        <div className="flex-1">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1.5 text-[12px] font-medium mb-6">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            Zgodny z Krajową Siecią Onkologiczną (KSO)
          </div>
          <h1 className="text-3xl xl:text-4xl font-bold leading-tight mb-4">
            Platforma konsyliów<br />onkologicznych
          </h1>
          <p className="text-white/75 text-[15px] leading-relaxed mb-10">
            Wideokonferencje MDT dla raka płuca i czerniaka. Sieć SOLO I–III, AI-wspomagane notatki, plany leczenia zgodne z KSO.
          </p>

          <div className="space-y-3">
            {[
              { icon: 'VideoIcon', title: 'Konsylia wideo SOLO I/II/III', desc: 'Wielopoziomowe sesje z nadzorem eksperckim' },
              { icon: 'BrainIcon', title: 'AI – automatyczne notatki', desc: 'Transkrypcja i podsumowania planów leczenia' },
              { icon: 'ShieldCheckIcon', title: 'Zgodność z KSO + 2FA', desc: 'Monitorowanie terminów i ochrona danych PHI' },
              { icon: 'FileTextIcon', title: 'Integracja e-DiLO', desc: 'Synchronizacja z kartą diagnostyki i leczenia' },
            ].map(f => (
              <div key={`feature-${f.icon}`} className="flex items-start gap-3 bg-white/10 rounded-xl p-3.5">
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon name={f.icon as 'HomeIcon'} size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold">{f.title}</p>
                  <p className="text-[12px] text-white/65 mt-0.5">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/20">
          <p className="text-[11px] text-white/50 text-center">
            Platforma referencyjna: onkologia.pacjent.gov.pl · MZ · CeZ<br />
            Ustawa KSO z dnia 9 marca 2023 r. (Dz.U.2024.1208 t.j.)
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 bg-background overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-bold text-xl text-primary">OncoConsil</span>
          </div>

          {/* 2FA security badge */}
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-5">
            <Icon name="ShieldCheckIcon" size={14} className="text-green-600 shrink-0" />
            <p className="text-[11px] text-green-700 font-medium">Ochrona PHI — Uwierzytelnianie dwuetapowe (TOTP) aktywne</p>
          </div>

          {/* Tabs */}
          <div className="flex bg-muted rounded-lg p-1 mb-6">
            {(['login', 'register'] as const).map(tab => (
              <button
                key={`tab-${tab}`}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2 rounded-md text-[13px] font-semibold transition-all duration-150 ${activeTab === tab ? 'bg-white text-primary shadow-card' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {tab === 'login' ? 'Logowanie' : 'Rejestracja'}
              </button>
            ))}
          </div>

          <h2 className="text-[22px] font-bold text-foreground mb-1">
            {activeTab === 'login' ? 'Zaloguj się do systemu' : 'Zarejestruj ośrodek'}
          </h2>
          <p className="text-[13px] text-muted-foreground mb-6">
            {activeTab === 'login' ? 'Dostęp do platformy konsyliów KSO' : 'Nowy ośrodek SOLO w sieci OncoConsil'}
          </p>

          {/* LOGIN FORM */}
          {activeTab === 'login' && (
            <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
              {/* Role */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">Rola kliniczna</label>
                <select
                  {...loginForm.register('role', { required: true })}
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-white text-[14px] text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                >
                  {roles.map(r => (
                    <option key={`role-${r.value}`} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              {/* SOLO Center */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">Ośrodek SOLO</label>
                <select
                  {...loginForm.register('soloCenter', { required: 'Wybierz ośrodek SOLO' })}
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-white text-[14px] text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                >
                  <option value="">— Wybierz ośrodek —</option>
                  {soloCenters.map(s => (
                    <option key={`solo-${s.value}`} value={s.value}>{s.label}</option>
                  ))}
                </select>
                {loginForm.formState.errors.soloCenter && <p className="text-[12px] text-destructive mt-1">{loginForm.formState.errors.soloCenter.message}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">Adres e-mail</label>
                <input
                  type="email"
                  {...loginForm.register('email', { required: 'Adres e-mail jest wymagany', pattern: { value: /\S+@\S+\.\S+/, message: 'Nieprawidłowy format e-mail' } })}
                  placeholder="lekarz@ośrodek.pl"
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-white text-[14px] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
                {loginForm.formState.errors.email && <p className="text-[12px] text-destructive mt-1">{loginForm.formState.errors.email.message}</p>}
              </div>

              {/* Password */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">Hasło</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...loginForm.register('password', { required: 'Hasło jest wymagane', minLength: { value: 8, message: 'Minimum 8 znaków' } })}
                    placeholder="••••••••"
                    className="w-full px-3 py-2.5 pr-10 rounded-lg border border-input bg-white text-[14px] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                  <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    <Icon name={showPassword ? 'EyeOffIcon' : 'EyeIcon'} size={16} />
                  </button>
                </div>
                {loginForm.formState.errors.password && <p className="text-[12px] text-destructive mt-1">{loginForm.formState.errors.password.message}</p>}
              </div>

              {/* Remember */}
              <div className="flex items-center gap-2">
                <input type="checkbox" id="remember" {...loginForm.register('remember')} className="w-4 h-4 rounded border-input accent-primary" />
                <label htmlFor="remember" className="text-[13px] text-muted-foreground">Zapamiętaj sesję (8 godzin)</label>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 rounded-lg bg-primary text-white text-[14px] font-semibold hover:bg-primary/90 active:scale-[0.98] transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <><Icon name="LoaderIcon" size={16} className="animate-spin" /><span>Logowanie...</span></>
                ) : (
                  <><Icon name="ShieldCheckIcon" size={16} /><span>Zaloguj się</span></>
                )}
              </button>
            </form>
          )}

          {/* REGISTER FORM */}
          {activeTab === 'register' && (
            <form onSubmit={registerForm.handleSubmit(onRegisterSubmit)} className="space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">Imię i nazwisko</label>
                <input
                  type="text"
                  {...registerForm.register('fullName', { required: 'Imię i nazwisko jest wymagane' })}
                  placeholder="dr Jan Kowalski"
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-white text-[14px] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
                {registerForm.formState.errors.fullName && <p className="text-[12px] text-destructive mt-1">{registerForm.formState.errors.fullName.message}</p>}
              </div>

              {/* Role */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">Rola kliniczna</label>
                <select
                  {...registerForm.register('role', { required: true })}
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-white text-[14px] text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                >
                  {roles.map(r => (
                    <option key={`reg-role-${r.value}`} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              {/* SOLO Center */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">Ośrodek SOLO</label>
                <select
                  {...registerForm.register('soloCenter', { required: 'Wybierz ośrodek SOLO' })}
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-white text-[14px] text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                >
                  <option value="">— Wybierz ośrodek —</option>
                  {soloCenters.map(s => (
                    <option key={`reg-solo-${s.value}`} value={s.value}>{s.label}</option>
                  ))}
                </select>
                {registerForm.formState.errors.soloCenter && <p className="text-[12px] text-destructive mt-1">{registerForm.formState.errors.soloCenter.message}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">Adres e-mail</label>
                <input
                  type="email"
                  {...registerForm.register('email', { required: 'Adres e-mail jest wymagany', pattern: { value: /\S+@\S+\.\S+/, message: 'Nieprawidłowy format e-mail' } })}
                  placeholder="lekarz@ośrodek.pl"
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-white text-[14px] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
                {registerForm.formState.errors.email && <p className="text-[12px] text-destructive mt-1">{registerForm.formState.errors.email.message}</p>}
              </div>

              {/* Password */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">Hasło</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...registerForm.register('password', { required: 'Hasło jest wymagane', minLength: { value: 8, message: 'Minimum 8 znaków' } })}
                    placeholder="••••••••"
                    className="w-full px-3 py-2.5 pr-10 rounded-lg border border-input bg-white text-[14px] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                  <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    <Icon name={showPassword ? 'EyeOffIcon' : 'EyeIcon'} size={16} />
                  </button>
                </div>
                {registerForm.formState.errors.password && <p className="text-[12px] text-destructive mt-1">{registerForm.formState.errors.password.message}</p>}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">Potwierdź hasło</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...registerForm.register('confirmPassword', { required: 'Potwierdzenie hasła jest wymagane' })}
                    placeholder="••••••••"
                    className="w-full px-3 py-2.5 pr-10 rounded-lg border border-input bg-white text-[14px] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    <Icon name={showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'} size={16} />
                  </button>
                </div>
                {registerForm.formState.errors.confirmPassword && <p className="text-[12px] text-destructive mt-1">{registerForm.formState.errors.confirmPassword.message}</p>}
              </div>

              {/* 2FA notice */}
              <div className="flex items-start gap-2 bg-primary/5 border border-primary/20 rounded-lg p-3">
                <Icon name="ShieldCheckIcon" size={15} className="text-primary mt-0.5 shrink-0" />
                <p className="text-[11px] text-muted-foreground">
                  Po rejestracji zostaniesz poproszona/y o skonfigurowanie <span className="font-semibold text-primary">uwierzytelniania dwuetapowego (TOTP)</span> w celu ochrony danych PHI pacjentów.
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 rounded-lg bg-primary text-white text-[14px] font-semibold hover:bg-primary/90 active:scale-[0.98] transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <><Icon name="LoaderIcon" size={16} className="animate-spin" /><span>Rejestrowanie...</span></>
                ) : (
                  <><Icon name="UserPlusIcon" size={16} /><span>Zarejestruj ośrodek</span></>
                )}
              </button>
            </form>
          )}

          {/* AppZDROWIE note */}
          <div className="mt-4 p-3 rounded-lg bg-accent/5 border border-accent/20 flex items-start gap-2">
            <Icon name="InfoIcon" size={15} className="text-accent mt-0.5 shrink-0" />
            <p className="text-[11px] text-muted-foreground">
              Integracja z <span className="font-semibold text-accent">AppZDROWIE / IKP</span> — logowanie przez profil zaufany dostępne od Q3 2026
            </p>
          </div>

          {/* Demo credentials — only on login tab */}
          {activeTab === 'login' && (
            <div className="mt-6">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Konta demonstracyjne</p>
              <div className="rounded-xl border border-border overflow-hidden">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="bg-muted/60">
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Rola</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">E-mail</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {demoCredentials.map((c, i) => (
                      <tr key={`demo-${i}`} className="border-t border-border hover:bg-muted/40 transition-colors">
                        <td className="px-3 py-2 text-foreground font-medium">{c.role}</td>
                        <td className="px-3 py-2 text-muted-foreground font-mono">{c.email}</td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => autofill(c.email, c.password)}
                            className="text-[11px] text-primary font-semibold hover:underline whitespace-nowrap"
                          >
                            Użyj
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground text-center mt-6">
            Korzystając z platformy akceptujesz{' '}
            <a href="#" className="text-primary underline">Regulamin</a> i{' '}
            <a href="#" className="text-primary underline">Politykę prywatności</a>
          </p>
        </div>
      </div>
    </div>
  );
}